import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "components/main/utils/Dialog";
import { CHECKOUT_FRAME_TARGET, closeCheckout } from "@/lib/paddle";

/// Past this the form is revealed as it stands rather than spin on an event
/// that never comes.
const REVEAL_TIMEOUT_MS = 10_000;

/// A Paddle frame rendered inline in our own dialog. It is mounted invisibly
/// and revealed on Paddle's load event: `frameTarget` resolves against the live
/// DOM and a re-parented iframe reloads itself.
export const PaddleFrameDialog: React.FC<{
  /// The whole effect key: `null` closes, and any NEW value opens a new frame.
  /// The caller picks the comparison by what it passes — object identity for a
  /// request, string equality for a transaction id.
  openKey: object | string | null;
  title: string;
  failureText: string;
  onClose: () => void;
  /// Opens the frame, calling `settle` on Paddle's load event. Read through a
  /// ref, so an inline arrow cannot re-run the effect and reopen the frame.
  openFrame: (settle: () => void) => Promise<void>;
  onOpenFailed: (error: unknown) => void;
  /// The reveal wait is over, however it ended.
  onSettled: () => void;
}> = ({
  openKey,
  title,
  failureText,
  onClose,
  openFrame,
  onOpenFailed,
  onSettled,
}) => {
  const [shown, setShown] = useState(false);
  const [failed, setFailed] = useState(false);

  const settled = useRef(onSettled);
  settled.current = onSettled;
  const open = useRef(openFrame);
  open.current = openFrame;
  const failure = useRef(onOpenFailed);
  failure.current = onOpenFailed;

  useEffect(() => {
    if (!openKey) return;
    setShown(false);
    setFailed(false);

    let done = false;
    const settle = (): void => {
      if (done) return;
      done = true;
      setShown(true);
      settled.current();
    };
    const timer = window.setTimeout(settle, REVEAL_TIMEOUT_MS);

    void open.current(settle).catch((e: unknown) => {
      failure.current(e);
      setFailed(true);
      settle();
    });

    return () => {
      window.clearTimeout(timer);
      void closeCheckout();
    };
  }, [openKey]);

  return (
    <Dialog
      open={openKey !== null}
      onClose={onClose}
      title={title}
      autoSpacing={false}
      size="medium"
      // Unpainted, not unmounted: the frame keeps its real size while Paddle fills it.
      backdropStyle={shown ? undefined : { opacity: 0, pointerEvents: "none" }}
    >
      <div className="relative min-h-112">
        <div className={CHECKOUT_FRAME_TARGET} />
        {failed && (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="text-error-regular">{failureText}</span>
          </span>
        )}
      </div>
    </Dialog>
  );
};
